// The Goblic channel's wire frames — see
// docs/plans/2026-09-30-runners/04-run-tools.md §2.1.
//
// One JSON object per line, in both directions, over the `goblic-relay`
// process's stdin/stdout (the worker demultiplexes by `id`, same as
// `execStream` already does for stdout/stderr). `ChannelRequestSchema` is
// what the sandbox side (`goblic-guard`, `goblic-tools`, the Pi extension)
// sends; `ChannelResponseSchema` is what the worker answers with.

import { z } from 'zod'

/** `guard.check` (02), `tool.call` ({name, args}, 04), `ping` (liveness). */
export const ChannelOpSchema = z.enum(['guard.check', 'tool.call', 'ping'])
export type ChannelOp = z.infer<typeof ChannelOpSchema>

/** One line of the wire protocol must not exceed this — the relay/server reject anything bigger. */
export const CHANNEL_FRAME_MAX_BYTES = 1024 * 1024

export const ChannelRequestSchema = z.object({
  id: z.string().min(1),
  token: z.string().min(1),
  op: ChannelOpSchema,
  args: z.unknown().optional(),
})
export type ChannelRequest = z.infer<typeof ChannelRequestSchema>

export const ChannelResponseSchema = z.object({
  id: z.string().min(1),
  ok: z.boolean(),
  result: z.unknown().optional(),
  error: z.string().min(1).optional(),
})
export type ChannelResponse = z.infer<typeof ChannelResponseSchema>

/** The `args` shape of a `tool.call` request (04 §2.1). */
export const ToolCallArgsSchema = z.object({
  name: z.string().min(1),
  args: z.unknown(),
})
export type ToolCallArgs = z.infer<typeof ToolCallArgsSchema>

/** Throwing parse — use when the worker/relay reads a request line. */
export function parseChannelRequest(input: unknown): ChannelRequest {
  return ChannelRequestSchema.parse(input)
}

export function safeParseChannelRequest(input: unknown) {
  return ChannelRequestSchema.safeParse(input)
}

/** Throwing parse — use when a sandbox-side client reads a response line. */
export function parseChannelResponse(input: unknown): ChannelResponse {
  return ChannelResponseSchema.parse(input)
}

export function safeParseChannelResponse(input: unknown) {
  return ChannelResponseSchema.safeParse(input)
}

/** Thrown by `ChannelLineSplitter` when a single line exceeds `CHANNEL_FRAME_MAX_BYTES`. */
export class ChannelFrameTooLargeError extends Error {
  constructor(byteLength: number) {
    super(`Goblic channel frame too large: ${byteLength} bytes (max ${CHANNEL_FRAME_MAX_BYTES})`)
    this.name = 'ChannelFrameTooLargeError'
  }
}

/**
 * Splits a decoded text stream into newline-delimited JSON frames (§2.1:
 * "één JSON per regel, max 1 MB"). Both ends of the Goblic channel share this
 * exact splitter — the worker's `ChannelServer` (reading the relay's
 * stdout) and the sandbox's `client.mjs`/`goblic-relay.mjs` (reading the
 * worker's stdin over the exec'd relay process) — so a frame valid on one
 * side is valid on the other. Browser-safe (no `Buffer`): byte length is
 * measured with `TextEncoder`, same as elsewhere in this package.
 */
export class ChannelLineSplitter {
  private buffer = ''

  /**
   * Feed one chunk of decoded text. Returns every complete line found
   * (without its trailing `\n` or `\r\n`); empty lines are dropped. Throws
   * `ChannelFrameTooLargeError` as soon as buffered, not-yet-terminated
   * input exceeds the limit — the caller should close the connection
   * rather than keep buffering an oversized or runaway frame.
   */
  push(chunk: string): string[] {
    this.buffer += chunk
    const lines: string[] = []
    let newlineIndex: number
    while ((newlineIndex = this.buffer.indexOf('\n')) !== -1) {
      const line = this.buffer.slice(0, newlineIndex).replace(/\r$/, '')
      this.buffer = this.buffer.slice(newlineIndex + 1)
      if (line.length === 0) continue
      assertFrameSize(line)
      lines.push(line)
    }
    assertFrameSize(this.buffer)
    return lines
  }

  /**
   * Discards whatever is buffered. `push` throws on an oversized frame
   * without clearing `buffer` first (the caller needs the byte count the
   * error carries) — a caller that keeps using this splitter after that
   * (a respawned relay connection reusing the same instance, say) must call
   * this first, or every subsequent `push` throws again on the same
   * leftover bytes.
   */
  reset(): void {
    this.buffer = ''
  }
}

function byteLength(text: string): number {
  return new TextEncoder().encode(text).length
}

function assertFrameSize(text: string): void {
  const size = byteLength(text)
  if (size > CHANNEL_FRAME_MAX_BYTES) throw new ChannelFrameTooLargeError(size)
}
